/**
 * Unit-vs-unit attack FX: counter-damage on the attacker slot (return fire).
 */

export function shouldHoldSurvivorHealthForAttackUnit(instanceId, healthBefore, healthAfter) {
  return Boolean(
    instanceId
    && typeof healthBefore === 'number'
    && typeof healthAfter === 'number'
    && healthAfter > 0
    && healthBefore > healthAfter
  );
}

/** Authoritative HP already shows the post-hit value — do not re-pin or replay countdown FX. */
export function isAttackSurvivorDamageAlreadyApplied(liveHealth, healthBefore, healthAfter) {
  if (typeof healthBefore !== 'number' || typeof healthAfter !== 'number') {
    return false;
  }
  if (typeof liveHealth !== 'number') {
    return false;
  }
  return liveHealth <= healthAfter && liveHealth < healthBefore;
}

export function resolveLiveUnitHealthByInstanceId(gameState, instanceId) {
  if (!gameState?.state || !instanceId) {
    return undefined;
  }
  for (const playerState of Object.values(gameState.state)) {
    const playedOrders = playerState?.playedOrders;
    if (!playedOrders) {
      continue;
    }
    for (const zone of ['front', 'flank']) {
      const row = playedOrders[zone];
      if (!Array.isArray(row)) {
        continue;
      }
      for (const unit of row) {
        if (unit?.instanceId === instanceId && typeof unit.health === 'number') {
          return unit.health;
        }
      }
    }
  }
  return undefined;
}

/** Stable key for a survivor HP step-down FX session (dedupe turn-boundary replays). */
export function buildSurvivorCountdownSessionKey(instanceId, healthBefore, toHealth) {
  if (!instanceId || typeof healthBefore !== 'number' || typeof toHealth !== 'number') {
    return null;
  }
  return `${instanceId}:${healthBefore}->${toHealth}`;
}

export function resolveTargetHealthAfterFromTrigger(trigger) {
  if (!trigger) {
    return undefined;
  }
  if (typeof trigger.healthAfter === 'number') {
    return trigger.healthAfter;
  }
  if (
    typeof trigger.healthBefore === 'number'
    && typeof trigger.damageAmount === 'number'
    && trigger.damageAmount > 0
  ) {
    return Math.max(0, trigger.healthBefore - trigger.damageAmount);
  }
  return undefined;
}

/**
 * Resolve survivor countdown metadata from a damage trigger + optional live lookup.
 * Returns null when no step-down countdown should run.
 */
export function resolveSurvivorCountdownFromTrigger(trigger, resolveToHealth) {
  if (!trigger?.targetInstanceId) {
    return null;
  }
  const healthBefore = typeof trigger.healthBefore === 'number'
    ? trigger.healthBefore
    : undefined;
  const derivedAfter = resolveTargetHealthAfterFromTrigger(trigger);
  let toHealth = typeof resolveToHealth === 'function'
    ? resolveToHealth(trigger)
    : derivedAfter;
  if (typeof toHealth !== 'number') {
    toHealth = derivedAfter;
  }
  if (
    typeof healthBefore !== 'number'
    || typeof toHealth !== 'number'
    || toHealth <= 0
    || healthBefore <= toHealth
  ) {
    return null;
  }
  return {
    instanceId: trigger.targetInstanceId,
    healthBefore,
    toHealth,
    zoneName: trigger.targetZone,
    slotIndex: trigger.targetIndex,
  };
}

export function resolveTargetHealthBeforeFromOutcome(outcome, actualUnitDamage, isGeneralAttack = false) {
  if (isGeneralAttack || !outcome) {
    return undefined;
  }
  if (typeof outcome.targetHealthBefore === 'number') {
    return outcome.targetHealthBefore;
  }
  if (typeof outcome.targetHealthAfter === 'number' && typeof actualUnitDamage === 'number') {
    return outcome.targetHealthAfter + actualUnitDamage;
  }
  return undefined;
}

/**
 * Pin survivor badge HP at pre-hit values before local attack-outcome preview mutates gameState.
 */
export function holdSurvivorHealthForAttackOutcome(outcome, setHealthHold, options = {}) {
  if (!outcome || typeof setHealthHold !== 'function') {
    return;
  }
  const isGeneralAttack = options.isGeneralAttack === true
    || outcome.isGeneralAttack === true
    || outcome.targetZone === 'general';
  if (isGeneralAttack) {
    return;
  }

  const actualUnitDamage = typeof options.getActualUnitDamage === 'function'
    ? options.getActualUnitDamage(outcome)
    : (
      typeof outcome.targetHealthBefore === 'number' && typeof outcome.targetHealthAfter === 'number'
        ? Math.max(0, outcome.targetHealthBefore - outcome.targetHealthAfter)
        : (typeof outcome.damageToTarget === 'number' ? outcome.damageToTarget : 0)
    );

  const resolveLiveHealthByInstanceId = typeof options.resolveLiveHealthByInstanceId === 'function'
    ? options.resolveLiveHealthByInstanceId
    : null;

  const maybeHoldSurvivor = (instanceId, healthBefore, healthAfter, slotOptions = {}) => {
    if (!shouldHoldSurvivorHealthForAttackUnit(instanceId, healthBefore, healthAfter)) {
      return;
    }
    if (resolveLiveHealthByInstanceId) {
      const liveHealth = resolveLiveHealthByInstanceId(instanceId);
      if (isAttackSurvivorDamageAlreadyApplied(liveHealth, healthBefore, healthAfter)) {
        return;
      }
    }
    setHealthHold(instanceId, healthBefore, healthAfter, slotOptions);
  };

  const targetHealthBefore = resolveTargetHealthBeforeFromOutcome(outcome, actualUnitDamage, false);
  const targetHealthAfter = typeof outcome.targetHealthAfter === 'number'
    ? outcome.targetHealthAfter
    : undefined;

  maybeHoldSurvivor(outcome.targetInstanceId, targetHealthBefore, targetHealthAfter);

  maybeHoldSurvivor(
    outcome.attackerInstanceId,
    outcome.attackerHealthBefore,
    outcome.attackerHealthAfter
  );
}

function attackHealthUnchanged(healthBefore, healthAfter) {
  return typeof healthBefore === 'number'
    && typeof healthAfter === 'number'
    && healthBefore === healthAfter;
}

/**
 * Target's shield fully blocked the hit (e.g. Defender of the Old Gods losing shield with no HP loss).
 */
export function resolveAttackTargetShieldDeflectedFromOutcome(outcome) {
  if (!outcome || outcome.isGeneralAttack === true || outcome.targetZone === 'general') {
    return false;
  }
  if (outcome.targetShieldConsumed !== true) {
    return false;
  }
  const damageToTarget = typeof outcome.damageToTarget === 'number' ? outcome.damageToTarget : 0;
  if (damageToTarget > 0) {
    return false;
  }
  if (
    typeof outcome.targetHealthBefore === 'number'
    && typeof outcome.targetHealthAfter === 'number'
    && outcome.targetHealthBefore !== outcome.targetHealthAfter
  ) {
    return false;
  }
  return true;
}

/**
 * Attacker's shield fully blocked return fire (counter-damage absorbed, shield consumed).
 */
export function resolveAttackAttackerShieldDeflectedFromOutcome(outcome) {
  if (!outcome || outcome.isGeneralAttack === true || outcome.targetZone === 'general') {
    return false;
  }
  if (!outcome.attackerInstanceId || outcome.attackerShieldConsumed !== true) {
    return false;
  }
  const damageToAttacker = typeof outcome.damageToAttacker === 'number' ? outcome.damageToAttacker : 0;
  if (damageToAttacker > 0) {
    return false;
  }
  if (
    typeof outcome.attackerHealthBefore === 'number'
    && typeof outcome.attackerHealthAfter === 'number'
    && outcome.attackerHealthBefore !== outcome.attackerHealthAfter
  ) {
    return false;
  }
  return attackHealthUnchanged(outcome.attackerHealthBefore, outcome.attackerHealthAfter)
    || (typeof outcome.attackerHealthBefore !== 'number' && typeof outcome.attackerHealthAfter !== 'number');
}

export function shouldQueueAttackerCounterDamageTrigger(outcome, isGeneralAttack = false) {
  if (isGeneralAttack || !outcome) {
    return false;
  }
  const damageToAttacker = typeof outcome.damageToAttacker === 'number' ? outcome.damageToAttacker : 0;
  return Boolean(
    damageToAttacker > 0
    && outcome.attackerInstanceId
    && typeof outcome.attackerHealthBefore === 'number'
    && typeof outcome.attackerHealthAfter === 'number'
    && outcome.attackerHealthBefore > outcome.attackerHealthAfter
  );
}

export function shouldQueueAttackerSlotDamageFxTrigger(outcome, isGeneralAttack = false) {
  return shouldQueueAttackerCounterDamageTrigger(outcome, isGeneralAttack)
    || resolveAttackAttackerShieldDeflectedFromOutcome(outcome);
}

export function buildAttackerCounterDamageTrigger({
  attackId,
  outcome,
  attackerZone,
  attackerIndex,
  anchor,
  isGeneralAttack = false,
}) {
  if (!anchor || !outcome) {
    return null;
  }

  if (shouldQueueAttackerCounterDamageTrigger(outcome, isGeneralAttack)) {
    const triggerKey = `${attackId}-attacker-counter`;
    const damageToAttacker = outcome.damageToAttacker;
    return {
      triggerKey,
      payload: {
        attackId: triggerKey,
        x: anchor.x,
        y: anchor.y,
        slotElement: anchor.slotElement,
        rect: anchor.rect,
        timestamp: Date.now(),
        targetZone: attackerZone,
        targetIndex: attackerIndex,
        damageAmount: damageToAttacker,
        targetInstanceId: outcome.attackerInstanceId,
        healthBefore: outcome.attackerHealthBefore,
        healthAfter: outcome.attackerHealthAfter,
      },
    };
  }

  if (!resolveAttackAttackerShieldDeflectedFromOutcome(outcome)) {
    return null;
  }

  const triggerKey = `${attackId}-attacker-shield-deflect`;
  return {
    triggerKey,
    payload: {
      attackId: triggerKey,
      x: anchor.x,
      y: anchor.y,
      slotElement: anchor.slotElement,
      rect: anchor.rect,
      timestamp: Date.now(),
      targetZone: attackerZone,
      targetIndex: attackerIndex,
      damageAmount: undefined,
      shieldDeflected: true,
      targetInstanceId: outcome.attackerInstanceId,
      healthBefore: outcome.attackerHealthBefore,
      healthAfter: outcome.attackerHealthAfter,
    },
  };
}

/** Engine slot zone (front/flank) → viewer-relative DOM zone (your_front / opponent_front). */
export function engineBattlefieldZoneToViewerZone(engineZone, unitOwnerPlayerId, viewerUserId) {
  if (engineZone !== 'front' && engineZone !== 'flank') {
    throw new Error(`engineBattlefieldZoneToViewerZone: unsupported zone "${engineZone}"`);
  }
  if (!unitOwnerPlayerId || !viewerUserId) {
    throw new Error('engineBattlefieldZoneToViewerZone: unitOwnerPlayerId and viewerUserId are required');
  }
  const prefix = String(unitOwnerPlayerId) === String(viewerUserId) ? 'your' : 'opponent';
  return `${prefix}_${engineZone}`;
}

export function resolveScatterDamageViewerTargets(scatterDamageTargets, viewerUserId) {
  if (!Array.isArray(scatterDamageTargets)) {
    return [];
  }
  if (!viewerUserId) {
    throw new Error('resolveScatterDamageViewerTargets: viewerUserId is required');
  }
  return scatterDamageTargets.map((entry) => {
    if (!entry?.targetInstanceId || entry.targetZone == null || entry.targetIndex == null) {
      throw new Error('resolveScatterDamageViewerTargets: invalid scatter damage entry');
    }
    if (!entry.targetPlayerId) {
      throw new Error(
        `resolveScatterDamageViewerTargets: missing targetPlayerId for ${entry.targetInstanceId}`
      );
    }
    return {
      ...entry,
      targetZone: engineBattlefieldZoneToViewerZone(
        entry.targetZone,
        entry.targetPlayerId,
        viewerUserId
      ),
    };
  });
}

/**
 * Pin pre-scatter HP for zone bystanders (primary target scatter is chained after combat countdown).
 */
export function holdScatterHealthForAttackOutcome(outcome, setHealthHold, viewerUserId) {
  if (!outcome || typeof setHealthHold !== 'function') {
    return;
  }
  const viewerTargets = resolveScatterDamageViewerTargets(outcome.scatterDamageTargets, viewerUserId);
  viewerTargets.forEach((target) => {
    if (target.isPrimaryAttackTarget) {
      return;
    }
    if (
      typeof target.healthBefore !== 'number'
      || typeof target.healthAfter !== 'number'
      || target.healthBefore <= target.healthAfter
    ) {
      return;
    }
    setHealthHold(
      target.targetInstanceId,
      target.healthBefore,
      target.healthAfter,
      {
        zoneName: target.targetZone,
        slotIndex: target.targetIndex,
      }
    );
  });
}

export function resolvePrimaryScatterFollowUp(outcome, viewerUserId) {
  const viewerTargets = resolveScatterDamageViewerTargets(outcome?.scatterDamageTargets, viewerUserId);
  return viewerTargets.find((target) => target.isPrimaryAttackTarget === true) ?? null;
}

export function resolveScatterBystanderTargets(outcome, viewerUserId) {
  return resolveScatterDamageViewerTargets(outcome?.scatterDamageTargets, viewerUserId)
    .filter((target) => !target.isPrimaryAttackTarget);
}

/**
 * True when attack-complete beat scatter hit FX — safe to pin pre-scatter holds before state lands.
 * queueAttackScatterAtHit clears the attackId entry once dive-hit scatter pops are queued.
 */
export function shouldReholdScatterBeforeAttackCompleteState(pendingScatterHoldsByAttackId, attackId) {
  if (!attackId || !pendingScatterHoldsByAttackId) {
    return false;
  }
  if (typeof pendingScatterHoldsByAttackId.has !== 'function') {
    throw new Error('shouldReholdScatterBeforeAttackCompleteState: pendingScatterHoldsByAttackId must be a Map');
  }
  return pendingScatterHoldsByAttackId.has(attackId);
}
